import { useEffect, useState } from "react";
import api from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const LANG_NAMES = { en: "English", es: "Spanish", vi: "Vietnamese", fr: "French", ar: "Arabic", hmn: "Hmong" };
const RANGES = [[7, "7 days"], [30, "30 days"], [90, "90 days"], [0, "All time"]];
const KINDS = [["all", "All pages"], ["county", "County pages"], ["city", "City pages"], ["index", "County list"]];
const selectCls = "h-9 rounded-sm border border-input bg-background px-2 text-sm";

const Seg = ({ items, value, onChange, testid }) => (
  <div className="inline-flex rounded-sm border border-border overflow-hidden text-xs font-semibold" data-testid={testid}>
    {items.map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
        className={`px-3 py-1.5 transition-colors ${value === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>{label}</button>
    ))}
  </div>
);

const LangCards = ({ total, byLang, lang, setLang }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="traffic-lang-cards">
    {[["all", "All languages", Object.values(byLang).reduce((a, b) => a + b, 0)], ...Object.keys(LANG_NAMES).map((k) => [k, LANG_NAMES[k], byLang[k] || 0])].map(([k, label, n]) => (
      <button key={k} type="button" onClick={() => setLang(k)} data-testid={`traffic-lang-${k}`}
        className={`grid-card p-4 text-left transition-colors ${lang === k ? "ring-2 ring-accent" : "hover:bg-secondary/50"}`}>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-serif text-2xl text-foreground">{n.toLocaleString()}</p>
      </button>
    ))}
    <span className="sr-only" data-testid="traffic-total">{total}</span>
  </div>
);

const Daily = ({ data }) => (
  <div className="grid-card p-5 mb-6 h-64" data-testid="traffic-daily-chart">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
        <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
        <Line type="monotone" dataKey="views" name="Page views" stroke="hsl(var(--accent))" strokeWidth={2} dot={data.length < 15} />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

const TopPages = ({ rows }) => (
  <div className="grid-card overflow-hidden" data-testid="traffic-top-pages">
    <p className="font-serif text-xl p-5 pb-2">Most-visited pages</p>
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
        <tr><th className="p-3">Page</th><th className="p-3">Type</th><th className="p-3">By language</th><th className="p-3 text-right">Views</th></tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map((r) => (
          <tr key={`${r.kind}-${r.county}-${r.city}`} data-testid={`traffic-row-${r.kind}-${r.county || "all"}-${r.city || ""}`}>
            <td className="p-3 font-medium">{r.name}</td>
            <td className="p-3 text-muted-foreground capitalize">{r.kind === "index" ? "list" : r.kind}</td>
            <td className="p-3"><div className="flex flex-wrap gap-1">{Object.entries(r.langs).sort((a, b) => b[1] - a[1]).map(([l, n]) => (
              <span key={l} className="text-[0.7rem] rounded-sm border border-border px-1.5 py-0.5">{l.toUpperCase()} {n}</span>))}</div></td>
            <td className="p-3 text-right font-semibold">{r.views.toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Sources = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => r.views));
  return (
    <div className="grid-card p-5" data-testid="traffic-sources">
      <p className="font-serif text-xl mb-1">Where visits came from</p>
      <p className="text-xs text-muted-foreground mb-4">"Direct" means typed in or bookmarked; "Internal" means clicked from another page on your site.</p>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.source} data-testid={`traffic-source-${r.source}`}>
            <div className="flex justify-between text-sm"><span>{r.source}</span><span className="font-semibold">{r.views.toLocaleString()}</span></div>
            <div className="h-1.5 bg-secondary rounded-full mt-1"><div className="h-full bg-accent rounded-full" style={{ width: `${(r.views / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function AdminTraffic() {
  const [days, setDays] = useState(30);
  const [lang, setLang] = useState("all");
  const [kind, setKind] = useState("all");
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/traffic", { params: { days, lang, kind } }).then(({ data }) => setData(data)).catch(() => setData({ total: 0, by_lang: {}, top: [], sources: [], daily: [] }));
  }, [days, lang, kind]);
  return (
    <div data-testid="admin-traffic">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="overline text-accent mb-1">Search traffic</p>
          <h1 className="font-serif text-3xl text-foreground">County & city page visits</h1>
          <p className="text-sm text-muted-foreground">Every page view of your county and city guides, by language and source. Search bots are left out.</p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <Seg items={RANGES} value={days} onChange={setDays} testid="traffic-range" />
          <select value={kind} onChange={(e) => setKind(e.target.value)} className={selectCls} data-testid="traffic-kind">
            {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      </div>
      {!data ? <p className="text-muted-foreground animate-pulse">Loading…</p> : (
        <>
          <LangCards total={data.total} byLang={data.by_lang} lang={lang} setLang={setLang} />
          {data.total === 0 ? <p className="grid-card p-8 text-muted-foreground" data-testid="traffic-empty">No visits in this period yet.</p> : (
            <>
              <Daily data={data.daily} />
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2"><TopPages rows={data.top} /></div>
                <Sources rows={data.sources} />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
